// CognitoCustomChallengeService.js
// Utilities for CUSTOM_AUTH (custom challenge) + attribute-based 2FA flows
// Works with both Webpack (process.env) and Vite (import.meta.env)

import {
  CognitoUserPool,
  CognitoUser,
  CognitoUserSession,
  CognitoIdToken,
  CognitoAccessToken,
  CognitoRefreshToken,
  CognitoUserAttribute,
  AuthenticationDetails,
} from 'amazon-cognito-identity-js';

// -------------------------
// Env + pool configuration
// -------------------------
const POOL_DATA = {
  UserPoolId: process.env.awsPoolId,
  ClientId: process.env.clientId,
};

function assertPoolData() {
  if (!POOL_DATA.UserPoolId || !POOL_DATA.ClientId) {
    throw new Error('Cognito pool config missing. Ensure UserPoolId/ClientId are defined in env.');
  }
}

// -------------------------
// Helpers
// -------------------------
function toStringRecord(obj = {}) {
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    // Drop undefined/null; coerce others to string
    if (v !== undefined && v !== null) out[k] = String(v);
  }
  return out;
}

function asAttributeList(attrsOrMap) {
  if (Array.isArray(attrsOrMap)) return attrsOrMap;
  const map = toStringRecord(attrsOrMap);
  return Object.entries(map).map(
    ([Name, Value]) => new CognitoUserAttribute({ Name, Value })
  );
}

// -------------------------
// User reconstruction
// -------------------------

/**
 * Rebuild a CognitoUser from tokens+username stored at login.
 * Returns null if any piece is missing.
 */
export function getCurrentUser() {
  assertPoolData();

  const idTokenValue = localStorage.getItem('idtoken');
  const accessTokenValue = localStorage.getItem('token');
  const refreshTokenValue = localStorage.getItem('refreshtoken');
  const usernameValue = localStorage.getItem('username');

  if (!idTokenValue || !accessTokenValue || !refreshTokenValue || !usernameValue) {
    return null;
  }

  const userPool = new CognitoUserPool(POOL_DATA);
  const user = new CognitoUser({ Username: usernameValue, Pool: userPool });

  const session = new CognitoUserSession({
    IdToken: new CognitoIdToken({ IdToken: idTokenValue }),
    AccessToken: new CognitoAccessToken({ AccessToken: accessTokenValue }),
    RefreshToken: new CognitoRefreshToken({ RefreshToken: refreshTokenValue }),
  });

  user.setSignInUserSession(session);
  return user;
}
export const userPool = new CognitoUserPool({
  UserPoolId: process.env.awsPoolId,
  ClientId: process.env.clientId,
});

/** Promise wrapper for user.getSession(...) */
export function getSession(cognitoUser) {
  return new Promise((resolve, reject) => {
    cognitoUser.getSession((err, session) => {
      if (err || !session) {
        // If session is invalid, try to refresh using refresh token
        const refreshTokenValue = localStorage.getItem('refreshtoken') || localStorage.getItem('refresh_token');
        if (refreshTokenValue) {
          refreshSession(cognitoUser, refreshTokenValue)
            .then(resolve)
            .catch(reject);
        } else {
          reject(err || new Error('Session is null'));
        }
      } else {
        resolve(session);
      }
    });
  });
}

/**
 * Refresh Cognito session using refresh token
 * @param {CognitoUser} cognitoUser - The Cognito user instance
 * @param {string} refreshTokenValue - The refresh token string
 * @returns {Promise<CognitoUserSession>}
 */
export function refreshSession(cognitoUser, refreshTokenValue) {
  return new Promise((resolve, reject) => {
    if (!cognitoUser || !refreshTokenValue) {
      return reject(new Error('Cognito user or refresh token is missing'));
    }

    const refreshToken = new CognitoRefreshToken({ RefreshToken: refreshTokenValue });
    
    cognitoUser.refreshSession(refreshToken, (err, session) => {
      if (err) {
        console.error('Error refreshing session:', err);
        // Clear tokens on refresh failure
        localStorage.removeItem('idtoken');
        localStorage.removeItem('token');
        localStorage.removeItem('refreshtoken');
        localStorage.removeItem('id_token');
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('serviceToken');
        reject(err);
      } else {
        // Update stored tokens
        const idToken = session.getIdToken().getJwtToken();
        const accessToken = session.getAccessToken().getJwtToken();
        const newRefreshToken = session.getRefreshToken().getToken();

        localStorage.setItem('idtoken', idToken);
        localStorage.setItem('token', accessToken);
        localStorage.setItem('refreshtoken', newRefreshToken);
        localStorage.setItem('id_token', idToken);
        localStorage.setItem('access_token', accessToken);
        localStorage.setItem('refresh_token', newRefreshToken);
        localStorage.setItem('serviceToken', accessToken);

        resolve(session);
      }
    });
  });
}

/**
 * Check if the access token is expired or about to expire
 * @param {string} accessToken - JWT access token
 * @param {number} bufferSeconds - Buffer time in seconds before expiration (default: 60)
 * @returns {boolean}
 */
export function isTokenExpired(accessToken, bufferSeconds = 60) {
  if (!accessToken) return true;

  try {
    // Decode JWT token (without verification)
    const payload = JSON.parse(atob(accessToken.split('.')[1]));
    const exp = payload.exp; // Expiration time in seconds
    const now = Math.floor(Date.now() / 1000); // Current time in seconds
    
    // Check if token is expired or will expire within buffer time
    return exp - now < bufferSeconds;
  } catch (error) {
    console.error('Error checking token expiration:', error);
    return true; // Assume expired if we can't parse
  }
}

/**
 * Get token expiration time in milliseconds
 * @param {string} accessToken - JWT access token
 * @returns {number|null} - Expiration time in milliseconds, or null if invalid
 */
export function getTokenExpirationTime(accessToken) {
  if (!accessToken) return null;

  try {
    const payload = JSON.parse(atob(accessToken.split('.')[1]));
    const exp = payload.exp; // Expiration time in seconds
    return exp * 1000; // Convert to milliseconds
  } catch (error) {
    console.error('Error getting token expiration time:', error);
    return null;
  }
}

/**
 * Calculate milliseconds until token should be refreshed (2 minutes before expiry)
 * @param {string} accessToken - JWT access token
 * @param {number} refreshBufferMinutes - Minutes before expiry to refresh (default: 2)
 * @returns {number|null} - Milliseconds until refresh time, or null if invalid/expired
 */
export function getTimeUntilRefresh(accessToken, refreshBufferMinutes = 2) {
  const expirationTime = getTokenExpirationTime(accessToken);
  if (!expirationTime) return null;

  const now = Date.now();
  const refreshTime = expirationTime - (refreshBufferMinutes * 60 * 1000); // 2 minutes before expiry
  
  // If refresh time has already passed, return 0 to refresh immediately
  if (refreshTime <= now) {
    return 0;
  }

  return refreshTime - now;
}

// Token refresh timer management
let tokenRefreshTimer = null;
let tokenRefreshTimeout = null;
let isTokenRefreshInProgress = false; // Flag to prevent concurrent refresh attempts
let isMonitorActive = false; // Flag to prevent multiple monitor instances

/**
 * Start automatic token refresh monitoring
 * Checks token expiry and refreshes 2 minutes before expiration
 */
export async function startTokenRefreshMonitor() {
  // Prevent multiple simultaneous monitor instances
  if (isMonitorActive) {
    console.log('Token refresh monitor is already active, skipping duplicate call');
    return;
  }

  // Clear any existing timers
  stopTokenRefreshMonitor();

  const accessToken = localStorage.getItem('token') || localStorage.getItem('access_token');
  const refreshTokenValue = localStorage.getItem('refreshtoken') || localStorage.getItem('refresh_token');

  if (!accessToken || !refreshTokenValue) {
    console.log('No tokens available for refresh monitoring');
    return;
  }

  const timeUntilRefresh = getTimeUntilRefresh(accessToken, 2); // 2 minutes before expiry
  if (timeUntilRefresh === null) {
    console.error('Unable to determine token refresh time');
    return;
  }

  isMonitorActive = true;
  console.log(`[TokenRefreshMonitor] Starting monitor (call count: ${(window.tokenRefreshCallCount || 0) + 1})`);
  window.tokenRefreshCallCount = (window.tokenRefreshCallCount || 0) + 1;

  if (timeUntilRefresh === 0) {
    // Token needs immediate refresh
    console.log('Token needs immediate refresh');
    await refreshTokenIfNeeded();
    // refreshTokenIfNeeded() will reschedule the monitor after refresh
    return;
  }

  console.log(`Token refresh scheduled in ${Math.round(timeUntilRefresh / 1000)} seconds (${Math.round(timeUntilRefresh / 60000)} minutes)`);

  // Set timeout to refresh token 2 minutes before expiry
  tokenRefreshTimeout = setTimeout(async () => {
    await refreshTokenIfNeeded();
    // After refresh, schedule the next refresh
    isMonitorActive = false; // Reset flag before recursive call
    startTokenRefreshMonitor();
  }, timeUntilRefresh);

  // Also set up a periodic check every 30 seconds as a safety net
  tokenRefreshTimer = setInterval(() => {
    if (isTokenRefreshInProgress) {
      return; // Skip if already refreshing
    }
    const currentToken = localStorage.getItem('token') || localStorage.getItem('access_token');
    if (currentToken && isTokenExpired(currentToken, 120)) { // Check if expiring within 2 minutes
      console.log('Periodic check: Token needs refresh');
      refreshTokenIfNeeded();
    }
  }, 30000); // Check every 30 seconds
}

/**
 * Stop automatic token refresh monitoring
 */
export function stopTokenRefreshMonitor() {
  if (tokenRefreshTimeout) {
    clearTimeout(tokenRefreshTimeout);
    tokenRefreshTimeout = null;
  }
  if (tokenRefreshTimer) {
    clearInterval(tokenRefreshTimer);
    tokenRefreshTimer = null;
  }
  isMonitorActive = false;
  console.log('[TokenRefreshMonitor] Monitor stopped');
}

/**
 * Refresh token if it's expired or about to expire
 * @returns {Promise<boolean>} - Returns true if refresh was successful, false otherwise
 */
export async function refreshTokenIfNeeded() {
  // Prevent concurrent refresh attempts
  if (isTokenRefreshInProgress) {
    console.log('Token refresh already in progress, skipping...');
    return false;
  }

  try {
    isTokenRefreshInProgress = true;
    const accessToken = localStorage.getItem('token') || localStorage.getItem('access_token');
    const refreshTokenValue = localStorage.getItem('refreshtoken') || localStorage.getItem('refresh_token');

    if (!refreshTokenValue) {
      console.warn('No refresh token available');
      isTokenRefreshInProgress = false;
      return false;
    }
    // Check if token needs refresh (expiring within 2 minutes)
    if (!isTokenExpired(accessToken, 121)) {
      console.log('Token is still valid, no refresh needed');
      isTokenRefreshInProgress = false;
      return true;
    }

    console.log('Refreshing token...');
    const cognitoUser = getCurrentUser();
    
    if (!cognitoUser) {
      console.error('No Cognito user available for token refresh');
      isTokenRefreshInProgress = false;
      return false;
    }

    await refreshSession(cognitoUser, refreshTokenValue);
    console.log('Token refreshed successfully');
    
    // Reschedule the monitor for the new token
    isMonitorActive = false; // Reset flag before recursive call
    startTokenRefreshMonitor();
    
    isTokenRefreshInProgress = false;
    return true;
  } catch (error) {
    console.error('Error refreshing token:', error);
    isTokenRefreshInProgress = false;
    return false;
  }
}

/**
 * Get and refresh session if needed
 * @param {CognitoUser} cognitoUser - The Cognito user instance
 * @returns {Promise<CognitoUserSession>}
 */
export async function getValidSession(cognitoUser) {
  const accessToken = localStorage.getItem('token') || localStorage.getItem('access_token');
  const refreshTokenValue = localStorage.getItem('refreshtoken') || localStorage.getItem('refresh_token');

  // Check if token is expired or about to expire
  if (isTokenExpired(accessToken)) {
    if (refreshTokenValue) {
      // Refresh the session
      return await refreshSession(cognitoUser, refreshTokenValue);
    } else {
      throw new Error('Token expired and no refresh token available');
    }
  }

  // Token is still valid, get current session
  return await getSession(cognitoUser);
}

/** Get attributes as a plain object: { name: value, ... } */
export function getAttributes() {
  return new Promise(async (resolve, reject) => {
    try {
      const user = getCurrentUser();
      if (!user) return reject(new Error('No user is currently authenticated.'));
      await getSession(user);
      user.getUserAttributes((err, result) => {  
        if (err) return reject(err);
        const obj = {};
        (result || []).forEach(a => { obj[a.getName()] = a.getValue(); });
        resolve(obj);
      });
    } catch (e) {
      reject(e);
    }
  });
}

/**
 * Update attributes. Accepts either:
 *  - array of CognitoUserAttribute, or
 *  - simple map { 'custom:key': 'value', ... }
 */
export function updateAttributes(cognitoUser, attributesOrMap) {
  const attributeList = asAttributeList(attributesOrMap);
  return new Promise((resolve, reject) => {
    cognitoUser.updateAttributes(attributeList, (err, res) => (err ? reject(err) : resolve(res)));
  });
}

// -------------------------
// Login (CUSTOM_AUTH) helpers (optional, for completeness)
// -------------------------

/**
 * Start password-based login using CUSTOM_AUTH (if your flow still needs it).
 * clientMetadata must be a string-only object (values coerced to strings).
 * Resolves either with a final session (onSuccess) OR with {challengeParameters, session}
 * when a customChallenge is returned by Cognito (i.e., needs OTP step).
 */
export function initiateCustomLogin(username, password, clientMetadata = {}) {
  assertPoolData();

  const userPool = new CognitoUserPool(POOL_DATA);
  const cognitoUser = new CognitoUser({ Username: username, Pool: userPool });
  const auth = new AuthenticationDetails({ Username: username, Password: password });
  const meta = toStringRecord(clientMetadata);

  cognitoUser.setAuthenticationFlowType('CUSTOM_AUTH');

  return new Promise((resolve, reject) => {
    cognitoUser.authenticateUser(
      auth,
      {
        onSuccess: (session) => resolve({ type: 'success', session, cognitoUser }),
        onFailure: (err) => reject(err),
        customChallenge: (challengeParameters) =>
          resolve({ type: 'challenge', challengeParameters, cognitoUser }),
      },
      meta // <-- ClientMetadata
    );
  });
}

// -------------------------
// CUSTOM_CHALLENGE OTP flows
// -------------------------

/**
 * Resend OTP for CUSTOM_CHALLENGE.
 * Triggers your Lambda to re-send the email OTP during CUSTOM_CHALLENGE.
 * Works with backends that look for either:
 *   1) a special ANSWER keyword (e.g., "__RESEND__"), or
 *   2) clientMetadata flags (e.g., { ans_type: "resend", factorName: "email" })
 *
 * @param {CognitoUser} cognitoUser - user tied to the current CUSTOM_CHALLENGE
 * @param {{ factorName?: string, email?: string, factorValue?: string }} [challengeParameters]
 * @returns {Promise<"resent">}
 */
// Resend OTP during CUSTOM_CHALLENGE
export function resendOTP(cognitoUser, challengeParameters = {}) {
  if (!cognitoUser || typeof cognitoUser.sendCustomChallengeAnswer !== 'function') {
    return Promise.reject(new Error('Cognito user is not initialized for custom challenge.'));
  }

  // ---- Ensure the challenge Session is present on this instance ----
  if (!cognitoUser.Session) {
    const saved = sessionStorage.getItem('mfa_session');
    if (saved) {
      try { cognitoUser.Session = saved; } catch { } // assign for this call
    }
  }
  if (!cognitoUser.Session) {
    return Promise.reject(new Error('Missing verification session. Please log in again.'));
  }

  const factorName = challengeParameters.factorName || 'email';
  const delivery = challengeParameters.email || challengeParameters.factorValue || '';

  // Wait for the actual result from sendCustomChallengeAnswer.
  // The callbacks will handle success/failure asynchronously.
  return new Promise((resolve, reject) => {
    const callbacks = {
      onSuccess: () => resolve('resent'),     // Backend accepts RESEND as success
      onFailure: (err) => reject(err),  
      customChallenge: (cp) =>
        resolve({ type: 'challenge', challengeParameters: cp, session: cognitoUser.Session }),
    };

    const meta = {
      ans_type: 'resend',
      factorName,
      factorValue: delivery,
    };

    try {
      // Prefer passing clientMetadata if supported by your Lambda
      if (cognitoUser.sendCustomChallengeAnswer.length >= 3) {
        cognitoUser.sendCustomChallengeAnswer('__RESEND__', callbacks, meta);
      } else {
        cognitoUser.sendCustomChallengeAnswer('__RESEND__', callbacks);
      }
      // Note: Do NOT resolve here - wait for onSuccess/onFailure callbacks
      // This ensures we properly handle errors like network failures, session expiration, etc.
    } catch (e) {
      reject(e);
    }
  });
}



/**
 * Submit OTP for CUSTOM_CHALLENGE.
 * In your Angular snippets, ans_type was 'wrong_otp' (kept for parity).
 */
// Verify OTP during CUSTOM_CHALLENGE
export function submitOTP(cognitoUser, otp, challengeParameters = {}) {
  if (!cognitoUser || typeof cognitoUser.sendCustomChallengeAnswer !== 'function') {
    return Promise.reject(new Error('Cognito user is not initialized for custom challenge.'));
  }

  // ---- Ensure the challenge Session is present on this instance ----
  if (!cognitoUser.Session) {
    const saved = sessionStorage.getItem('mfa_session');
    if (saved) {
      try { cognitoUser.Session = saved; } catch { } // attach for this call
    }
  }
  if (!cognitoUser.Session) {
    return Promise.reject(new Error('Your verification session expired. Please log in again.'));
  }

  // ---- Basic OTP hygiene ----
  const code = String(otp || '').replace(/\s+/g, '');
  if (!/^\d{6}$/.test(code)) {
    return Promise.reject(new Error('Please enter the 6-digit code.'));
  }

  // ---- Client metadata (optional) ----
  // If your Lambda expects flags, keep them; otherwise you can set meta = undefined.
  const meta = {
    ans_type: 'answer', // <-- do NOT send 'wrong_otp' for normal verification
    factorName: challengeParameters.factorName || 'email',
    factorValue: challengeParameters.factorValue || challengeParameters.email || '',
  };

  return new Promise((resolve, reject) => {
    const callbacks = {
      // Correct code -> tokens issued
      onSuccess: (session) => resolve({ type: 'success', session }),
      // Wrong code (but still in challenge) -> some SDK builds call customChallenge again
      customChallenge: (cp) =>
        resolve({ type: 'challenge', challengeParameters: cp, session: cognitoUser.Session }),
      // Fatal errors
      onFailure: (err) => reject(err),
    };

    try {
      // Pass clientMetadata if the SDK signature supports it
      if (cognitoUser.sendCustomChallengeAnswer.length >= 3) {
        cognitoUser.sendCustomChallengeAnswer(code, callbacks, meta);
      } else {
        cognitoUser.sendCustomChallengeAnswer(code, callbacks);
      }
    } catch (e) {
      reject(e);
    }
  });
}


// -------------------------
// Attribute verification for enabling 2FA
// -------------------------

/** Triggers a verification code for the given factor: 'email' or 'phone_number'. */
export function startAttributeVerification(cognitoUser, factor /* 'email' | 'phone_number' */) {
  return new Promise((resolve, reject) => {
    cognitoUser.getAttributeVerificationCode(factor, {
      onSuccess: resolve,
      onFailure: reject,
      inputVerificationCode: () => resolve(), // code entry happens on your OTP page
    });
  });
}

/** Verifies the attribute with the code the user typed. */
export function verifyAttribute(cognitoUser, factor, code) {
  return new Promise((resolve, reject) => {
    cognitoUser.verifyAttribute(factor, code, {
      onSuccess: resolve,
      onFailure: reject,
    });
  });
}

/* =========================
   2FA Prompt Decision (supports BOTH attributes)
   ========================= */

export const MFA_COOLDOWN_DAYS = 7; // 7-day cooldown period after user clicks "No Thanks"

/** Accepts epoch-seconds (string) or ISO-8601 strings; returns Date or null */
function parseCognitoTimestamp(ts) {
  if (!ts) return null;
  if (/^\d+$/.test(ts)) { // epoch seconds
    const n = Number(ts);
    if (!Number.isFinite(n)) return null;
    return new Date(n * 1000);
  }
  const d = new Date(ts);
  return Number.isFinite(d.getTime()) ? d : null;
}

/**
 * Decide whether to show the 2FA prompt using BOTH attributes.
 * Semantics:
 * - If mfa_reminder === 'false'  → NEVER show (explicit opt-out).
 * - Else (true or missing) → user is eligible; respect cooldown from mfa_denied_timestamp.
 * - If denied ts is missing/invalid → show.
 *
 * @param {Record<string,string>} attrMap
 * @param {boolean} isMfaAlreadyEnabled
 * @param {number} cooldownDays
 * @returns {boolean}
 */
export function shouldShowMfaPromptFromAttributes(attrMap, isMfaAlreadyEnabled, cooldownDays = MFA_COOLDOWN_DAYS) {
  if (isMfaAlreadyEnabled) return false;

  const reminder = (attrMap['custom:mfa_reminder'] ?? 'true').toLowerCase();
  if (reminder === 'false') return false;

  const deniedRaw = attrMap['custom:mfa_denied_timestamp'];
  const deniedDate = parseCognitoTimestamp(deniedRaw);
  if (!deniedDate) return true; // never denied → show

  const now = Date.now();
  const elapsedMs = now - deniedDate.getTime();
  const neededMs = cooldownDays * 24 * 60 * 60 * 1000;
  return elapsedMs >= neededMs; // show only after cooldown expires
}

/**
 * Mark "No thanks" (snooze). Keeps mfa_reminder = 'true', records a real timestamp.
 * Stores epoch-seconds to be compact and unambiguous.
 */
export async function markMfaNoThanks(cognitoUser) {
  await getSession(cognitoUser);
  const nowEpoch = Math.floor(Date.now() / 1000).toString();
  return updateAttributes(cognitoUser, {
    'custom:mfa_reminder': 'true',
    'custom:mfa_denied_timestamp': nowEpoch,
  });
}

/**
 * Mark "Never ask again" (permanent opt-out).
 * Sets mfa_reminder = 'false'. Timestamp is optional; we keep it for audit.
 */
export async function markMfaNeverAsk(cognitoUser) {
  await getSession(cognitoUser);
  const nowEpoch = Math.floor(Date.now() / 1000).toString();
  return updateAttributes(cognitoUser, {
    'custom:mfa_reminder': 'false',
    'custom:mfa_denied_timestamp': nowEpoch,
  });
}

/**
 * Optional helper to clear flags after successful 2FA enable.
 */
export async function clearMfaFlags(cognitoUser) {
  await getSession(cognitoUser);
  return updateAttributes(cognitoUser, {
    'custom:mfa_reminder': 'false',
    'custom:mfa_denied_timestamp': '',
  });
}

/* =========================
   NEW: TOTP (Yes, enable) helpers — CUSTOM_AUTH safe
   ========================= */

/** Begin TOTP association and return { secret, otpauth } for QR rendering */
export function startTotpAssociation(cognitoUser) {
  return new Promise(async (resolve, reject) => {
    try {
      await getSession(cognitoUser); // required under CUSTOM_AUTH

      const onOk = (respOrSecret) => {
        // SDK variations: sometimes object with SecretCode, sometimes raw string
        const secret = (respOrSecret && (respOrSecret.SecretCode || respOrSecret.secretCode)) || respOrSecret;
        if (!secret) return reject(new Error('No secret from associateSoftwareToken'));
        const username = localStorage.getItem('username') || 'user';
        const issuer = encodeURIComponent('Insight');
        const label = encodeURIComponent(username);
        const otpauth = `otpauth://totp/${issuer}:${label}?secret=${secret}&issuer=${issuer}&algorithm=SHA1&digits=6&period=30`;
        resolve({ secret, otpauth });
      };

      try {
        // object-callback style
        cognitoUser.associateSoftwareToken({ onSuccess: onOk, onFailure: reject });
      } catch {
        // node-style (err, data)
        cognitoUser.associateSoftwareToken((err, data) => (err ? reject(err) : onOk(data)));
      }
    } catch (e) {
      reject(e);
    }
  });
}

/** Verify the 6-digit TOTP the user typed */
export function verifyTotp(cognitoUser, code) {
  return new Promise(async (resolve, reject) => {
    try {
      await getSession(cognitoUser);
      const deviceName = 'Insight Device';

      try {
        // object-callback style
        cognitoUser.verifySoftwareToken(code, deviceName, {
          onSuccess: () => resolve(true),
          onFailure: reject,
        });
      } catch {
        // node-style
        cognitoUser.verifySoftwareToken(code, deviceName, (err) => (err ? reject(err) : resolve(true)));
      }
    } catch (e) {
      reject(e);
    }
  });
}

/** Set software token as the preferred MFA method */
export function setTotpPreferred(cognitoUser) {
  return new Promise(async (resolve, reject) => {
    try {
      await getSession(cognitoUser);
      cognitoUser.setUserMfaPreference(
        null,                   // SMS not enabled here
        { PreferredMfa: true }, // Software token
        (err, result) => (err ? reject(err) : resolve(result))
      );
    } catch (e) {
      reject(e);
    }
  });
}

/** Mark MFA active using your existing custom attributes */
export async function markMfaActive(cognitoUser) {
  await getSession(cognitoUser);
  return updateAttributes(cognitoUser, {
    'custom:mfa_state': 'active',
    'custom:mfa_reminder': 'false',
    'custom:mfa_denied_timestamp': '',
  });
}


/**
 * Begin login with OTP (supports passwordless CUSTOM_CHALLENGE or MFA OTP).
 * - username: email/phone
 * - password: optional (leave undefined if using passwordless/custom challenge)
 * - useCustomChallenge: true for passwordless email OTP, false for username+password MFA
 */
export function beginOtpLogin({ username, password, useCustomChallenge = true,
  clientMetadata = {}
}) {
  const cognitoUser = new CognitoUser({ Username: username, Pool: userPool });
  localStorage.setItem('email', username);

  if (useCustomChallenge) {
    cognitoUser.setAuthenticationFlowType("CUSTOM_AUTH");
    const authDetails = new AuthenticationDetails({ Username: username });
    return new Promise((resolve, reject) => {
      cognitoUser.initiateAuth(authDetails, {
        onSuccess: (session) => resolve({ status: "DONE", session, cognitoUser }),
        onFailure: reject,
        customChallenge: (challengeParameters) => {
          resolve({ status: "CHALLENGE", challengeName: "CUSTOM_CHALLENGE", challengeParameters, cognitoUser, clientMetadata });
        }
      });
    });
  }

  // Standard username/password login with MFA OTP challenge
  const authDetails = new AuthenticationDetails({ Username: username, Password: password || "" });
  return new Promise((resolve, reject) => {
    cognitoUser.authenticateUser(authDetails, {
      onSuccess: (session) => resolve({ status: "DONE", session, cognitoUser }),
      onFailure: reject,
      mfaRequired: (challengeName, challengeParameters) =>
        resolve({ status: "CHALLENGE", challengeName, challengeParameters, cognitoUser }),
      totpRequired: (challengeName, challengeParameters) =>
        resolve({ status: "CHALLENGE", challengeName, challengeParameters, cognitoUser }),
      selectMFAType: (challengeName, challengeParameters) =>
        resolve({ status: "CHALLENGE", challengeName, challengeParameters, cognitoUser, clientMetadata })
    });
  });
}

/**
 * Verify the OTP entered by the user.
 * - cognitoUser: object returned by beginOtpLogin
 * - challengeName: CUSTOM_CHALLENGE | EMAIL_OTP | SMS_MFA | SOFTWARE_TOKEN_MFA
 * - code: user-entered OTP
 * - rememberDevice: true/false for "trust this browser"
 */
export function verifyOtp({
  cognitoUser,
  challengeName = "CUSTOM_CHALLENGE",
  code,
  // rememberDevice = true,
  clientMetadata = { ans_type: "wrong_otp" }   // ← NEW: default to match prod
}) {
  return new Promise((resolve, reject) => {
    function persistTokensForAllKeys(session, username) {
      const idToken = session.getIdToken().getJwtToken();
      const accessToken = session.getAccessToken().getJwtToken();
      const refreshToken = session.getRefreshToken().getToken();

      const kv = {
        idtoken: idToken,
        token: accessToken,
        refreshtoken: refreshToken,
      };
      Object.entries(kv).forEach(([k, v]) => localStorage.setItem(k, v));
      if (username) localStorage.setItem("username", username);
    }

    const finish = function (session) {
      // 1) Persist tokens & flag V2
      try {
        persistTokensForAllKeys(session, cognitoUser.getUsername && cognitoUser.getUsername());
        localStorage.setItem('userExp', 'v2');
      } catch (e) {
        console.error('[verifyOtp] persistTokens error:', e);
      }
    
      // 2) If the OC token module isn’t present, resolve and exit
      resolve({ status: 'DONE', session: session });
    };
    

    // Helper for MFA flows (SMS/TOTP/EMAIL_OTP)
    const send = (name) => {
      // NOTE: clientMetadata is the 4th param for sendMFACode
      cognitoUser.sendMFACode(String(code || "").trim(), {
        onSuccess: finish,
        onFailure: reject
      }, name, clientMetadata);
    };

    switch (challengeName) {
      case "SMS_MFA": return send("SMS_MFA");
      case "SOFTWARE_TOKEN_MFA": return send("SOFTWARE_TOKEN_MFA");
      case "EMAIL_OTP": return send("EMAIL_OTP");

      case "CUSTOM_CHALLENGE":
        // NOTE: clientMetadata is the 3rd param for sendCustomChallengeAnswer
        cognitoUser.sendCustomChallengeAnswer(String(code || "").trim(), {
          onSuccess: finish,
          onFailure: reject,
          customChallenge: (challengeParameters) => {
            resolve({
              status: "CHALLENGE",
              challengeName: "CUSTOM_CHALLENGE",
              challengeParameters
            });
          }
        }, clientMetadata);
        return;

      default:
        return send();
    }
  });
}


/* =========================
   Default export
   ========================= */
const CognitoCustomChallengeService = {
  // session & attributes
  getCurrentUser,
  getSession,
  refreshSession,
  isTokenExpired,
  getTokenExpirationTime,
  getTimeUntilRefresh,
  getValidSession,
  startTokenRefreshMonitor,
  stopTokenRefreshMonitor,
  refreshTokenIfNeeded,
  getAttributes,
  updateAttributes,

  // login / custom auth
  initiateCustomLogin,
  resendOTP,
  submitOTP,

  // attribute verification
  startAttributeVerification,
  verifyAttribute,

  // 2FA prompt / attribute strategy
  shouldShowMfaPromptFromAttributes,
  MFA_COOLDOWN_DAYS,
  markMfaNoThanks,
  markMfaNeverAsk,
  clearMfaFlags,

  // TOTP helpers
  startTotpAssociation,
  verifyTotp,
  setTotpPreferred,
  markMfaActive,

  //OTP Login
  verifyOtp,
  beginOtpLogin,
};

export default CognitoCustomChallengeService;
